import React from 'react';
import {useLocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {TotalsSectionDataHooks} from '../TotalsSection';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Text, TextPriority} from 'wix-ui-tpa/cssVars';
import {classes} from './TaxIncludedRow.st.css';
import {SPECS} from '../../../../../common/constants';
import {getTaxesWithSomeTaxExemption, isPartialTaxExempt} from '../../../../../domain/utils/total.util';
import {TotalUpdateSkeleton} from '../../TotalUpdateSkeleton';
import {getSingleTaxIncludedText, getTaxIncludedRowText} from './Tax.utils';
import {TaxModel} from '../../../../../domain/models/checkout/CartSummary/Tax.model';

export const TaxIncludedRow = () => {
  const {cart, cartSummary, isCartSummaryUpdating} = useControllerProps().checkoutStore;
  const {checkoutSettings} = useControllerProps().checkoutSettingsStore;
  const localeKeys = useLocaleKeys();
  const {experiments} = useExperiments();
  const supportTaxExemptionOnCheckout = experiments.enabled(SPECS.SupportTaxExemptionOnCheckout);
  const taxIncludedInPrice = cart.taxInfo.pricesIncludeTax;
  const taxes = cartSummary!.taxSummary.taxes;
  const hasSomeTaxExemption = supportTaxExemptionOnCheckout && getTaxesWithSomeTaxExemption(taxes).length > 0;

  if (
    !experiments.enabled(SPECS.ShowPriceSummaryByTaxIncluded) ||
    !taxIncludedInPrice ||
    (!cartSummary!.hasTax && !hasSomeTaxExemption)
  ) {
    return null;
  }

  const hasTaxWithPartialExemption = (tax: TaxModel) => supportTaxExemptionOnCheckout && isPartialTaxExempt(tax);
  const hasOneTaxWithPartialExemption = taxes.length === 1 && hasTaxWithPartialExemption(taxes[0]);
  if (taxes.length === 1 && !hasTaxWithPartialExemption(taxes[0])) {
    const tax = taxes[0];
    return (
      <Text
        data-hook={TotalsSectionDataHooks.TaxIncluded}
        className={classes.taxIncludedText}
        priority={TextPriority.secondary}>
        {isCartSummaryUpdating ? (
          <div className={classes.taxIncludedTextLoader}>
            <TotalUpdateSkeleton size="medium" />
          </div>
        ) : (
          getSingleTaxIncludedText({
            tax,
            checkoutSettings,
            localeKeys,
            supportTaxExemptionOnCheckout,
            moveCheckoutToViewerContext: experiments.enabled(SPECS.MoveCheckoutToViewerContext),
          })
        )}
      </Text>
    );
  }

  return (
    <div className={classes.taxSectionContainer}>
      <Text
        data-hook={TotalsSectionDataHooks.TaxIncludedSectionTitle}
        className={classes.taxIncludedText}
        priority={TextPriority.secondary}>
        {hasOneTaxWithPartialExemption
          ? localeKeys.checkout.order_summary.totals.taxGroups.breakdown.taxExemption.label()
          : localeKeys.checkout.order_summary.totals.taxGroupsDisclaimer.title()}
      </Text>
      {taxes.map((tax, index) => (
        <Text
          key={index}
          data-hook={TotalsSectionDataHooks.TaxIncludedTaxRow}
          className={classes.taxIncludedText}
          priority={TextPriority.secondary}>
          {isCartSummaryUpdating ? (
            <div className={classes.taxIncludedTextLoader}>
              <TotalUpdateSkeleton />
            </div>
          ) : (
            getTaxIncludedRowText({
              tax,
              checkoutSettings,
              localeKeys,
              supportTaxExemptionOnCheckout,
              hasOneTaxWithPartialExemption,
              moveCheckoutToViewerContext: experiments.enabled(SPECS.MoveCheckoutToViewerContext),
            })
          )}
        </Text>
      ))}
    </div>
  );
};
